import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Typography,
  Spin,
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Tag,
  Space,
  Button,
  Select,
  InputNumber,
  Switch,
  Input,
  Popconfirm,
  message,
  Descriptions,
  Alert,
} from "antd";
import { EditOutlined } from "@ant-design/icons";

import {
  privateAdminGetApi,
  privateAdminPutApi,
  privateAdminPostApi,
  privateAdminDeleteApi,
} from "../../utils/apiHelper";

const { Title, Text } = Typography;

const LIMIT_FIELDS = [
  { key: "emailsPerMonth", label: "Emails / month" },
  { key: "emailsPerDay", label: "Emails / day" },
  { key: "contactsPerMonth", label: "Contacts / month (0 = unlimited)" },
  { key: "campaignsPerMonth", label: "Campaigns / month" },
  { key: "totalContacts", label: "Total contacts" },
  { key: "submissionsPerMonth", label: "Form submissions / month" },
];

function getAdminRole() {
  try {
    return JSON.parse(localStorage.getItem("adminUser") || "{}")?.role;
  } catch {
    return undefined;
  }
}

function trialSummary(startedAt, endsAt) {
  if (!startedAt && !endsAt) return "No trial";
  const end = endsAt
    ? new Date(endsAt)
    : startedAt
      ? new Date(startedAt)
      : null;
  if (!end) return "No trial";
  const active = endsAt ? new Date() < new Date(endsAt) : true;
  return `${active ? "Active" : "Ended"} — started ${
    startedAt ? new Date(startedAt).toLocaleDateString() : "?"
  }${endsAt ? `, ends ${new Date(endsAt).toLocaleDateString()}` : ""}`;
}

function PlanLimitsCard({ organization, onUpdated }) {
  const canEdit = getAdminRole() === "superadmin";
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState(organization.plan);
  const [formsPlan, setFormsPlan] = useState(organization.formsPlan);
  const [limits, setLimits] = useState({ ...organization.limits });

  useEffect(() => {
    setPlan(organization.plan);
    setFormsPlan(organization.formsPlan);
    setLimits({ ...organization.limits });
  }, [organization]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await privateAdminPutApi(
        `admin/organizations/${organization._id}`,
        { plan, formsPlan, limits },
      );
      message.success("Organization updated.");
      setEditing(false);
      onUpdated(res.data.result);
    } catch {
      // privateAdminPutApi already shows an error toast
    } finally {
      setSaving(false);
    }
  };

  const startTrial = async (product) => {
    const field = product === "forms" ? "formsTrialStartedAt" : "emailTrialStartedAt";
    try {
      const res = await privateAdminPutApi(
        `admin/organizations/${organization._id}`,
        { [field]: new Date().toISOString() },
      );
      message.success(`${product === "forms" ? "Forms" : "Email"} trial started.`);
      onUpdated(res.data.result);
    } catch {
      // toast already shown
    }
  };

  return (
    <Card
      size="small"
      title="Plan & limits"
      extra={
        canEdit &&
        (editing ? (
          <Space>
            <Button size="small" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="small" type="primary" loading={saving} onClick={save}>
              Save
            </Button>
          </Space>
        ) : (
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        ))
      }
    >
      <Descriptions bordered size="small" column={1}>
        <Descriptions.Item label="Email plan">
          {editing ? (
            <Select
              size="small"
              value={plan}
              style={{ width: 120 }}
              options={[
                { value: "free", label: "free" },
                { value: "paid", label: "paid" },
              ]}
              onChange={setPlan}
            />
          ) : (
            <Tag>{organization.plan}</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Forms plan">
          {editing ? (
            <Select
              size="small"
              value={formsPlan}
              style={{ width: 120 }}
              options={[
                { value: "free", label: "free" },
                { value: "paid", label: "paid" },
              ]}
              onChange={setFormsPlan}
            />
          ) : (
            <Tag>{organization.formsPlan}</Tag>
          )}
        </Descriptions.Item>
        {LIMIT_FIELDS.map(({ key, label }) => (
          <Descriptions.Item label={label} key={key}>
            {editing ? (
              <InputNumber
                size="small"
                min={0}
                value={limits[key]}
                onChange={(v) => setLimits((prev) => ({ ...prev, [key]: v }))}
              />
            ) : (
              (organization.limits?.[key] ?? "-")
            )}
          </Descriptions.Item>
        ))}
        <Descriptions.Item label="Email trial">
          <Space>
            <Text>
              {trialSummary(
                organization.emailTrialStartedAt,
                organization.emailTrialEndsAt,
              )}
            </Text>
            {canEdit && (
              <Button size="small" onClick={() => startTrial("email")}>
                Start / restart trial
              </Button>
            )}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Forms trial">
          <Space>
            <Text>
              {trialSummary(
                organization.formsTrialStartedAt,
                organization.formsTrialEndsAt,
              )}
            </Text>
            {canEdit && (
              <Button size="small" onClick={() => startTrial("forms")}>
                Start / restart trial
              </Button>
            )}
          </Space>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
}

const SENDER_STATUS_COLORS = {
  SUCCESS: "green",
  PENDING: "orange",
  FAILED: "red",
  NOT_STARTED: "default",
};

function SenderBrandingCard({ organization, organizationId, onUpdated }) {
  const canEdit = getAdminRole() === "superadmin";
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [emailFooter, setEmailFooter] = useState(organization.emailFooter || "");
  const [removeWhitelabel, setRemoveWhitelabel] = useState(
    organization.removeWhitelabel === true,
  );

  useEffect(() => {
    setEmailFooter(organization.emailFooter || "");
    setRemoveWhitelabel(organization.removeWhitelabel === true);
  }, [organization]);

  const saveBranding = async () => {
    setSaving(true);
    try {
      const res = await privateAdminPutApi(
        `admin/organizations/${organizationId}`,
        { emailFooter, removeWhitelabel },
      );
      message.success("Branding updated.");
      setEditing(false);
      onUpdated(res.data.result);
    } catch {
      // toast already shown
    } finally {
      setSaving(false);
    }
  };

  const syncSenderIdentity = async () => {
    setSyncing(true);
    try {
      const res = await privateAdminPostApi(
        `admin/organizations/${organizationId}/sender-identity/sync`,
        {},
      );
      message.success("Sender identity status re-checked.");
      onUpdated({ senderIdentity: res.data.result.senderIdentity });
    } catch {
      // toast already shown
    } finally {
      setSyncing(false);
    }
  };

  const resetSenderIdentity = async () => {
    try {
      const res = await privateAdminDeleteApi(
        `admin/organizations/${organizationId}/sender-identity`,
      );
      message.success("Sender verification reset.");
      onUpdated({
        senderIdentity: res.data.result.senderIdentity,
        smtpEmailAddress: res.data.result.smtpEmailAddress,
      });
    } catch {
      // toast already shown
    }
  };

  const senderIdentity = organization.senderIdentity || {};

  return (
    <Card size="small" title="Sender identity & branding">
      <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Sender email address">
          {organization.smtpEmailAddress || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Verification status">
          <Space>
            <Tag color={SENDER_STATUS_COLORS[senderIdentity.status] || "default"}>
              {senderIdentity.status || "NOT_STARTED"}
            </Tag>
            {senderIdentity.identity && (
              <Text type="secondary">
                {senderIdentity.type}: {senderIdentity.identity}
              </Text>
            )}
            {canEdit && senderIdentity.identity && (
              <>
                <Button size="small" loading={syncing} onClick={syncSenderIdentity}>
                  Recheck status
                </Button>
                <Popconfirm
                  title="Reset this organization's sender verification?"
                  description="Deletes the SES identity and clears verification records. The customer will need to re-verify."
                  onConfirm={resetSenderIdentity}
                >
                  <Button size="small" danger>
                    Reset verification
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        </Descriptions.Item>
        {senderIdentity.verificationRecords?.length > 0 && (
          <Descriptions.Item label="DNS / DKIM records">
            <Table
              size="small"
              pagination={false}
              rowKey="name"
              dataSource={senderIdentity.verificationRecords}
              columns={[
                { title: "Type", dataIndex: "type", key: "type" },
                { title: "Name", dataIndex: "name", key: "name", ellipsis: true },
                { title: "Value", dataIndex: "value", key: "value", ellipsis: true },
              ]}
            />
          </Descriptions.Item>
        )}
      </Descriptions>

      <Card
        size="small"
        type="inner"
        title="Email footer branding"
        extra={
          canEdit &&
          (editing ? (
            <Space>
              <Button size="small" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button
                size="small"
                type="primary"
                loading={saving}
                onClick={saveBranding}
              >
                Save
              </Button>
            </Space>
          ) : (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
          ))
        }
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Space>
            <Text>Remove Mailinflow footer branding:</Text>
            {editing ? (
              <Switch checked={removeWhitelabel} onChange={setRemoveWhitelabel} />
            ) : (
              <Tag color={organization.removeWhitelabel ? "green" : "default"}>
                {organization.removeWhitelabel ? "Removed" : "Shown"}
              </Tag>
            )}
          </Space>
          <div>
            <Text type="secondary">Custom footer HTML</Text>
            {editing ? (
              <Input.TextArea
                rows={4}
                value={emailFooter}
                onChange={(e) => setEmailFooter(e.target.value)}
              />
            ) : (
              <pre
                style={{
                  margin: 0,
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                  maxHeight: 120,
                  overflow: "auto",
                }}
              >
                {organization.emailFooter || "(none)"}
              </pre>
            )}
          </div>
        </Space>
      </Card>
    </Card>
  );
}

// PRD item 1 (FR-1.11), task 1.10.
function DeliverabilityCard({ organizationId }) {
  const canEdit = getAdminRole() === "superadmin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unpausing, setUnpausing] = useState(false);

  const fetchDeliverability = () => {
    setLoading(true);
    privateAdminGetApi(`admin/organizations/${organizationId}/deliverability`)
      .then((res) => {
        setData(res.data.result);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchDeliverability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const unpause = async () => {
    setUnpausing(true);
    try {
      await privateAdminPostApi(
        `admin/organizations/${organizationId}/deliverability/unpause`,
        {},
      );
      message.success("Sending resumed for this organization.");
      fetchDeliverability();
    } catch {
      // toast already shown
    } finally {
      setUnpausing(false);
    }
  };

  return (
    <Card
      size="small"
      title="Deliverability"
      extra={
        <Button size="small" loading={loading} onClick={fetchDeliverability}>
          Refresh
        </Button>
      }
    >
      {loading && !data ? (
        <Spin />
      ) : !data ? (
        <Text type="secondary">Failed to load deliverability status.</Text>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {data.pausedAt && (
            <Alert
              type="error"
              showIcon
              message="Sending paused"
              description={data.pausedReason || "This organization's sends are paused due to a high complaint rate."}
              action={
                canEdit && (
                  <Popconfirm
                    title="Resume sending for this organization?"
                    onConfirm={unpause}
                  >
                    <Button size="small" danger loading={unpausing}>
                      Unpause
                    </Button>
                  </Popconfirm>
                )
              }
            />
          )}
          {!data.pausedAt && data.warningAt && (
            <Alert
              type="warning"
              showIcon
              message="Elevated complaint rate"
              description="This organization's complaint rate is above the warning threshold (0.1% over 7 days)."
            />
          )}

          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Statistic
                title="Complaint rate (7d)"
                value={(data.complaintRate7d * 100).toFixed(2)}
                suffix="%"
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Bounce rate (7d)"
                value={(data.bounceRate7d * 100).toFixed(2)}
                suffix="%"
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Suppressed contacts" value={data.suppressedCount} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Sender"
                valueRender={() => (
                  <Tag color={data.onSharedSender ? "orange" : "green"}>
                    {data.onSharedSender ? "Shared subdomain" : "Own domain"}
                  </Tag>
                )}
              />
            </Col>
          </Row>
        </Space>
      )}
    </Card>
  );
}

// PRD item 3, task 3.8. Read-only, for support investigations only -- no
// mutation route exists for consentevents anywhere (it's an append-only
// ledger, see plugins/consentCore.js), so this is a plain fetch-and-render.
function ConsentLedgerCard({ organizationId }) {
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    privateAdminGetApi(`admin/organizations/${organizationId}/consent-events`)
      .then((res) => {
        setRecords(res.data.result.records || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [organizationId]);

  return (
    <Card size="small" title={`Consent ledger${records ? ` (${records.length})` : ""}`}>
      <Table
        rowKey="_id"
        size="small"
        loading={loading}
        pagination={{ pageSize: 10 }}
        dataSource={records || []}
        columns={[
          {
            title: "When",
            dataIndex: "occurredAt",
            key: "occurredAt",
            render: (v) => new Date(v).toLocaleString(),
          },
          { title: "Email", dataIndex: "emailAddress", key: "emailAddress" },
          { title: "Purpose", dataIndex: "purpose", key: "purpose" },
          {
            title: "Event",
            dataIndex: "event",
            key: "event",
            render: (event) => <Tag>{event}</Tag>,
          },
          {
            title: "Source",
            key: "source",
            render: (_, r) => r.source?.type || "-",
          },
          {
            title: "Actor",
            key: "actor",
            render: (_, r) => r.actor?.type || "-",
          },
        ]}
      />
    </Card>
  );
}

export default function AdminOrganizationDetail() {
  const { id } = useParams();
  const canEdit = getAdminRole() === "superadmin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [togglingFormId, setTogglingFormId] = useState(null);

  const fetchOrg = () => {
    privateAdminGetApi(`admin/organizations/${id}`)
      .then((res) => {
        setData(res.data.result);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const cancelCampaign = async (campaignId) => {
    setCancellingId(campaignId);
    try {
      await privateAdminPostApi(
        `admin/organizations/${id}/campaigns/${campaignId}/cancel`,
        {},
      );
      message.success("Campaign cancelled.");
      fetchOrg();
    } catch {
      // toast already shown
    } finally {
      setCancellingId(null);
    }
  };

  const toggleFormEnabled = async (formId, nextEnabled) => {
    setTogglingFormId(formId);
    try {
      await privateAdminPutApi(`admin/forms/${formId}/enabled`, {
        enabled: nextEnabled,
      });
      message.success(nextEnabled ? "Form re-enabled." : "Form disabled.");
      fetchOrg();
    } catch {
      // toast already shown
    } finally {
      setTogglingFormId(null);
    }
  };

  useEffect(() => {
    fetchOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (!data) {
    return <Text type="secondary">Failed to load organization.</Text>;
  }

  const { organization, campaigns, forms, recentSubmissions, members } = data;

  const handleOrgUpdated = (patch) => {
    setData((prev) => ({
      ...prev,
      organization: { ...prev.organization, ...patch },
    }));
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Link to="/admin/organizations">&larr; Back to organizations</Link>
        <Title level={5} style={{ marginTop: 8, marginBottom: 0 }}>
          {organization.name}
        </Title>
        <Text type="secondary">
          Created {new Date(organization.createdAt).toLocaleDateString()} by{" "}
          {organization.createdBy}
        </Text>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Email plan"
              valueRender={() => <Tag>{organization.plan}</Tag>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Forms plan"
              valueRender={() => <Tag>{organization.formsPlan}</Tag>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic title="Emails sent" value={data.emailsSent} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic title="Contacts" value={data.contactCount} />
          </Card>
        </Col>
      </Row>

      <PlanLimitsCard organization={organization} onUpdated={handleOrgUpdated} />
      <SenderBrandingCard
        organization={organization}
        organizationId={organization._id}
        onUpdated={handleOrgUpdated}
      />
      <DeliverabilityCard organizationId={organization._id} />

      {(organization.razorpaySubscriptionId ||
        organization.razorpayFormsSubscriptionId) && (
        <Card size="small" title="Billing">
          <Text>
            Email subscription: {organization.razorpaySubscriptionId || "none"}
          </Text>
          <br />
          <Text>
            Forms subscription:{" "}
            {organization.razorpayFormsSubscriptionId || "none"}
          </Text>
        </Card>
      )}

      <Card size="small" title="Members">
        <Table
          rowKey="userId"
          size="small"
          pagination={false}
          dataSource={members}
          columns={[
            {
              title: "Email",
              dataIndex: "emailAddress",
              key: "emailAddress",
              render: (email, r) =>
                r.userId ? (
                  <Link to={`/admin/users/${r.userId}`}>{email}</Link>
                ) : (
                  email
                ),
            },
            {
              title: "Name",
              key: "name",
              render: (_, r) => `${r.firstName || ""} ${r.lastName || ""}`.trim() || "-",
            },
            { title: "Role", dataIndex: "role", key: "role" },
            { title: "Status", dataIndex: "status", key: "status" },
          ]}
        />
      </Card>

      <ConsentLedgerCard organizationId={organization._id} />

      <Card size="small" title={`Campaigns (${campaigns.length})`}>
        <Table
          rowKey="_id"
          size="small"
          pagination={{ pageSize: 10 }}
          dataSource={campaigns}
          columns={[
            { title: "Subject", dataIndex: "subject", key: "subject" },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              render: (v, r) => (
                <Space>
                  <span>{v?.description || "-"}</span>
                  {r.cancelled && <Tag color="red">Cancelled</Tag>}
                </Space>
              ),
            },
            {
              title: "Created",
              dataIndex: "createdAt",
              key: "createdAt",
              render: (v) => new Date(v).toLocaleString(),
            },
            ...(canEdit
              ? [
                  {
                    title: "Actions",
                    key: "actions",
                    render: (_, r) =>
                      r.cancelled ? null : (
                        <Popconfirm
                          title="Cancel this campaign?"
                          description="Sends already queued may still go out, but no further sends will start."
                          onConfirm={() => cancelCampaign(r._id)}
                        >
                          <Button
                            size="small"
                            danger
                            loading={cancellingId === r._id}
                          >
                            Cancel
                          </Button>
                        </Popconfirm>
                      ),
                  },
                ]
              : []),
          ]}
        />
      </Card>

      <Card size="small" title={`Forms (${forms.length})`}>
        <Table
          rowKey="_id"
          size="small"
          pagination={false}
          dataSource={forms}
          columns={[
            { title: "Name", dataIndex: "name", key: "name" },
            {
              title: "Enabled",
              dataIndex: "enabled",
              key: "enabled",
              render: (v) => (v ? "Yes" : "No"),
            },
            { title: "Loads", dataIndex: "loads", key: "loads" },
            {
              title: "Tokens issued",
              dataIndex: "tokenIssued",
              key: "tokenIssued",
            },
            {
              title: "Submissions",
              dataIndex: "submissions",
              key: "submissions",
            },
            {
              title: "Conversion",
              key: "conversion",
              render: (_, r) =>
                r.loads ? `${Math.round((r.submissions / r.loads) * 100)}%` : "-",
            },
            {
              title: "Created",
              dataIndex: "createdAt",
              key: "createdAt",
              render: (v) => new Date(v).toLocaleString(),
            },
            ...(canEdit
              ? [
                  {
                    title: "Actions",
                    key: "actions",
                    render: (_, r) => (
                      <Popconfirm
                        title={r.enabled ? "Disable this form?" : "Re-enable this form?"}
                        description={
                          r.enabled
                            ? "It will stop accepting new submissions immediately."
                            : undefined
                        }
                        onConfirm={() => toggleFormEnabled(r._id, !r.enabled)}
                      >
                        <Button
                          size="small"
                          danger={r.enabled}
                          loading={togglingFormId === r._id}
                        >
                          {r.enabled ? "Disable" : "Enable"}
                        </Button>
                      </Popconfirm>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>

      <Card size="small" title="Recent submissions">
        <Table
          rowKey="_id"
          size="small"
          pagination={{ pageSize: 10 }}
          dataSource={recentSubmissions}
          columns={[
            { title: "Email", dataIndex: "email", key: "email" },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              render: (v) => <Tag>{v}</Tag>,
            },
            {
              title: "Created",
              dataIndex: "createdAt",
              key: "createdAt",
              render: (v) => new Date(v).toLocaleString(),
            },
          ]}
        />
      </Card>
    </Space>
  );
}
